import React, { useState } from 'react';
import ContentSection from 'components/shared/ContentSection';
import useEvents from 'sdk/hooks/events/useEvents';
import NoContent from 'components/shared/NoContent/NoContent';
import { format } from 'date-fns';
import clsx from 'clsx';
import ReactPaginate from 'react-paginate';
// import useGetAssetByName from 'sdk/hooks/assets/useGetAssetByName';
import ViewAll from 'components/shared/ViewAll';
import styles from './EventsTable.module.scss';

export type EventsTableProps = {
  name: string,
  date: Date,
  search?: string,
  page: number,
  short?: boolean,
}

export type EventsTableContainerProps = {
  name: string,
  date: Date,
  search?: string,
  count: number,
  short?: boolean,
  link?: string,
}

export type EventsItem = {
  eventType: string,
  data: any,
  index: number,
  page: number,
}

function EventItem({
  eventType,
  data,
  index,
  page,
} : EventsItem) {
  // const { data: asset } = useGetAssetByName(data?.Symbol);

  if (eventType === 'IPOs') {
    return (
      <tr className={styles.tr}>
        <td>{(page * 10) + index + 1}</td>
        <td>
          <div className={styles.nameContainer}>
            {/* asset?.icon ? (
              <img className={styles.logo} src={asset?.icon} alt={asset?.ticker} />
            ) : (
              <div className={styles.logoPlaceholder} />
            ) */}
            <span className={styles.name}>
              {`${data?.Symbol} (${data?.Company})`}
            </span>
          </div>
        </td>
        <td>{data?.Exchange}</td>
        <td>{data?.['Price Range']}</td>
        <td>{data?.Price}</td>
        <td>{data?.Currency}</td>
        <td>{data?.Shares}</td>
        <td>{data?.Actions}</td>
      </tr>
    )
  }
  if (eventType === 'Stock Splits') {
    return (
      <tr className={styles.tr}>
        <td>{(page * 10) + index + 1}</td>
        <td>
          <div className={styles.nameContainer}>
            {/* asset?.icon ? (
              <img className={styles.logo} src={asset?.icon} alt={asset?.ticker} />
            ) : (
              <div className={styles.logoPlaceholder} />
            ) */}
            <span className={styles.name}>
              {`${data?.Symbol} (${data?.Company})`}
            </span>
          </div>
        </td>
        <td>{data?.['Payable on']}</td>
        <td>{data?.['Optionable?']}</td>
        <td>{data?.Ratio}</td>
      </tr>
    )
  }
  if (eventType === 'Economic Events') {
    return (
      <tr className={styles.tr}>
        <td>{(page * 10) + index + 1}</td>
        <td>{data?.Event}</td>
        <td>{data?.Country}</td>
        <td>{data?.['Event Time']}</td>
        <td>{data?.For}</td>
        <td>{data?.Actual}</td>
        <td>{data?.['Market Expectation']}</td>
        <td>{data?.['Prior to This']}</td>
        <td>{data?.['Revised from']}</td>
      </tr>
    )
  }

  const percent = (percentString: string) => {
    if (Number(percentString) >= 0) {
      return `+${Number(percentString)}`
    }
    return percentString;
  }

  return (
    <tr className={styles.tr}>
      <td>{(page * 10) + index + 1}</td>
      <td>
        <div className={styles.nameContainer}>
          {/* asset?.icon ? (
            <img className={styles.logo} src={asset?.icon} alt={asset?.ticker} />
          ) : (
            <div className={styles.logoPlaceholder} />
          ) */}
          <span className={styles.name}>
            {`${data?.Symbol} (${data?.Company})`}
          </span>
        </div>
      </td>
      <td>{data?.['Event Name']}</td>
      <td>{data?.['Earnings Call Time']}</td>
      <td>{data?.['EPS Estimate']}</td>
      <td>{data?.['Reported EPS']}</td>
      <td
        className={clsx(
          styles.tdPercent,
          Number(data?.['Surprise(%)']) < 0 && styles.tdPercentMinus,
        )}
      >
        {percent(data?.['Surprise(%)'])}
      </td>
    </tr>
  )
}

function EventsTable({
  name,
  date,
  search,
  page,
  short,
} : EventsTableProps) {
  const { data, isLoading } = useEvents({
    name,
    date,
    search,
    page,
  })

  if (short && data?.length) {
    data.length = data.length < 3 ? data.length : 3;
  }

  if (!data?.length && !isLoading && !short) {
    return (
      <NoContent
        className={styles.noContent}
        message="No events were found"
      />
    )
  }

  if (name === 'IPOs') {
    return (
      <table className={styles.table}>
        <tbody>
          <tr className={styles.tr}>
            <th>#</th>
            <th>Company</th>
            <th>Exchange</th>
            <th>Price Range</th>
            <th>Price</th>
            <th>Currency</th>
            <th>Shares</th>
            <th>Actions</th>
          </tr>
          {isLoading && Array.from(Array(short ? 3 : 10).keys())?.map((item) => (
            <tr key={item} className={styles.tr}>
              <td colSpan={7} />
              <td />
            </tr>
          ))}
          {data?.map((item, index) => (
            <EventItem
              eventType={name}
              data={item}
              index={index}
              page={page}
            />
          ))}
        </tbody>
      </table>
    )
  }

  if (name === 'Stock Splits') {
    return (
      <table className={styles.table}>
        <tbody>
          <tr className={styles.tr}>
            <th>#</th>
            <th>Company</th>
            <th>Payable on</th>
            <th>Optionable?</th>
            <th>Ratio</th>
          </tr>
          {isLoading && Array.from(Array(short ? 3 : 10).keys())?.map((item) => (
            <tr key={item} className={styles.tr}>
              <td colSpan={4} />
              <td />
            </tr>
          ))}
          {data?.map((item, index) => (
            <EventItem
              eventType={name}
              data={item}
              index={index}
              page={page}
            />
          ))}
        </tbody>
      </table>
    )
  }

  if (name === 'Economic Events') {
    return (
      <table className={styles.table}>
        <tbody>
          <tr className={styles.tr}>
            <th>#</th>
            <th>Event</th>
            <th>Country</th>
            <th>Event Time</th>
            <th>For</th>
            <th>Actual</th>
            <th>Market Expectation</th>
            <th>Prior to This</th>
            <th>Revised from</th>
          </tr>
          {isLoading && Array.from(Array(short ? 3 : 10).keys())?.map((item) => (
            <tr key={item} className={styles.tr}>
              <td colSpan={8} />
              <td />
            </tr>
          ))}
          {data?.map((item, index) => (
            <EventItem
              eventType={name}
              data={item}
              index={index}
              page={page}
            />
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <table className={styles.table}>
      <tbody>
        <tr className={styles.tr}>
          <th>#</th>
          <th>Company</th>
          <th>Event Name</th>
          <th>Earnings Call Time</th>
          <th>EPS Estimate</th>
          <th>Reported EPS</th>
          <th>Surprise(%)</th>
        </tr>
        {isLoading && Array.from(Array(short ? 3 : 10).keys())?.map((item) => (
          <tr key={item} className={styles.tr}>
            <td colSpan={6} />
            <td />
          </tr>
        ))}
        {data?.map((item, index) => (
          <EventItem
            eventType={name}
            data={item}
            index={index}
            page={page}
          />
        ))}
      </tbody>
    </table>
  )
}

function EventsTableContainer({
  name, date, search, count, short, link,
} : EventsTableContainerProps) {
  let title = `${name} on ${format(date, 'EEE, MMM dd')}`
  if (short) {
    title = name
  }
  const [page, setPage] = useState<number>(0);
  const pageCount = Math.ceil(count / 10);
  const handlePageClick = (event) => {
    setPage(event.selected)
  };

  if (search) {
    title = `Showing ${name} for: ${search}`
  }

  return (
    <ContentSection
      className={styles.contentSection}
      title={title}
      headerRight={link && (
        <ViewAll url={link} />
      )}
      smallTitle
    >
      <EventsTable
        name={name}
        date={date}
        search={search}
        page={page}
        short={short}
      />
      {(pageCount > 1 && !short) && (
        <ReactPaginate
          className={styles.navigation}
          breakLabel="..."
          nextLabel=""
          onPageChange={handlePageClick}
          pageRangeDisplayed={3}
          marginPagesDisplayed={1}
          pageCount={pageCount}
          previousLabel=""
          renderOnZeroPageCount={null}
          pageClassName={styles.navigationPage}
          pageLinkClassName={styles.navigationLink}
          activeClassName={styles.navigationPageActive}
          breakLinkClassName={styles.navigationBreak}
          nextLinkClassName={styles.navigationNext}
          previousLinkClassName={styles.navigationPrev}
        />
      )}
    </ContentSection>
  )
}

export default EventsTableContainer;
